<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：CC(電話)サブ画面
 *
 * 作成者：2021/11/25 ノア）黄
 * 最終修正者：2021/11/25 ノア）黄
 *
 -->
<template>
  <ttiDialog
    :isShow="isShow"
    @handelDisplay="onHandelDisplay"
    :manualKey="manualKey"
    :title="$t('phoneCC.title')"
    width="450px"
  >
    <template slot="body">
      <p class="tti-phonecc-label">
        {{ $t('phoneCC.contents') }}
      </p>
      <el-form ref="phoneCCFormRef" :model="phoneCCForm" :rules="phoneCCRules" label-width="90px">
        <el-form-item prop="tel" :label="$t('phoneCC.phoneNumber')">
          <el-input v-model.trim="phoneCCForm.tel" size="small"></el-input>
        </el-form-item>
        <el-form-item prop="userName" :label="$t('phoneCC.name')">
          <el-input v-model.trim="phoneCCForm.userName" size="small"></el-input>
        </el-form-item>
      </el-form>
    </template>
    <template slot="foot">
      <el-button
        class="tti-btn-default tti-btn-001"
        @click="onExecuteClick"
        v-tooltip="{
          content: `${$t('phoneCC.executeTip')}`,
          placement: 'bottom-start',
          delay: { show: 1000, hide: 0 }
        }"
      >
        {{ $t('phoneCC.execute') }}</el-button
      >
    </template>
  </ttiDialog>
</template>

<script lang="ts">
import { Component, Vue, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { isEmpty } from 'lodash'

import config from '@/config/config'
import { dialog } from '@/store/modules'
import TtiDialog from '@/components/TtiDialog.vue'
import manualConfig from '@/config/manualConfig'
import { PhoneCC } from '@/models'
import { i18n } from '@/locales'
import { RULES, VALID_PATTERN, validateService } from '@/service/validateService'
import { getCaller, updateCaller, postLogout } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { clearLocal, clearSession } from '@/commons/services/localService'
import { moveService } from '@/commons/services/moveService'
import HelperService from '@/service/helperService'
import { clearWindowCookie } from '@/service/refreshAndCloseWindowService'

@Component({
  name: 'home-cc',
  components: {
    TtiDialog
  }
})
export default class HOME_CC extends Vue {
  /**
   * CC(電話)フォームのref
   */
  @Ref() readonly phoneCCFormRef!: Form

  /**
   * CC(電話)サブ画面の表示フラグを取得
   *
   * @returns {boolean} CC(電話)サブ画面の表示フラグ
   */
  get isShow(): boolean {
    if (dialog.isPhoneCCDialogShow) {
      this.openDialogBefore()
    } else {
      this.closeDialogAfter()
    }
    return dialog.isPhoneCCDialogShow
  }

  /**
   * CC(電話)サブ画面の表示フラグを設定する
   *
   * @param {boolean} value CC(電話)サブ画面の表示フラグ
   */
  set isShow(value: boolean) {
    dialog.setPhoneCCDialogShow(value)
  }

  /**
   * 表示状態変更
   *
   * @param {boolean} value 表示状態
   * @returns {void}
   */
  onHandelDisplay(value: boolean): void {
    dialog.setPhoneCCDialogShow(value)
  }

  /**
   * CC(電話)サブ画面のマニュアルキーを取得
   *
   * @returns {string} CC(電話)サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.phoneCC
  }

  /**
   * CC(電話)Form
   */
  phoneCCForm: PhoneCC = {
    userName: '',
    tel: ''
  }

  /**
   * CC(電話)検証ルール
   */
  phoneCCRules: Form['rules'] = {
    // 電話番号
    tel: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('phoneCC.phoneNumber')]).toString(),
        trigger: 'blur'
      },
      // TEL
      {
        pattern: VALID_PATTERN[RULES.TEL],
        message: i18n.t('vaildate.common.alphanumericAndSymbols', [i18n.t('phoneCC.phoneNumber')]).toString(),
        trigger: 'blur'
      },
      // 16文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 16,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('phoneCC.phoneNumber'), 16]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 名前
    userName: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('phoneCC.name')]).toString(),
        trigger: 'blur'
      },
      // 20文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 20,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('phoneCC.name'), 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * 実行ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onExecuteClick(): void {
    this.phoneCCFormRef.validate((valid: boolean) => {
      if (!valid) return false

      this.updateCallerData(this.phoneCCForm)
    })
  }

  /**
   * CC(電話)を取得する処理
   *
   * @returns {void}
   */
  async fetchCallerData(): Promise<void> {
    try {
      this.phoneCCForm = await getCaller()

      if (isEmpty(this.phoneCCForm)) {
        this.goLogout()
      }
    } catch (e) {
      this.$log.debug('HOME_CC > fetchCallerData: e=', e)
      throw e
    }
  }

  /**
   * 画面を閉じ、ログアウト
   *
   * @returns {void}
   */
  async goLogout(): Promise<void> {
    this.onHandelDisplay(false)

    await moveService.beforeLogout()
    this.doLogout()
  }

  /**
   * ログアウトを実行する。
   *
   * @returns {void}
   */
  async doLogout(): Promise<void> {
    try {
      await postLogout()
      clearLocal()
      clearSession()
      HelperService.removeAccessToken()
      clearWindowCookie()
      Vue.nextTick(() => {
        this.$router.push(config.menu.login)
      })
    } catch (e) {
      Vue.$log.debug('Login > onLogout: e=', e)
      throw e
    }
  }

  /**
   * CC(電話)更新する処理
   *
   * @param {PhoneCC} updateCallerParam CC(電話)のパラメータ
   * @returns {void}
   */
  async updateCallerData(updateCallerParam: PhoneCC): Promise<void> {
    try {
      await updateCaller(updateCallerParam)
      this.updateAfter()
    } catch (e) {
      this.$log.debug('HOME_CC > updateCallerData: e=', e)
      throw e
    }
  }

  /**
   * CC(電話)更新後に実行したい操作。
   *
   * @returns {void}
   */
  updateAfter(): void {
    msgBoxService.info(i18n.t('phoneCC.callSuccess').toString())
  }

  /**
   * ダイアログを開く前に実行したい操作。
   *
   * @returns {void}
   */
  openDialogBefore(): void {
    this.fetchCallerData()
  }

  /**
   * ダダイアログを閉じる後に実行したい操作。
   *
   * @returns {void}
   */
  closeDialogAfter(): void {
    this.phoneCCForm = {
      userName: '',
      tel: ''
    }
  }
}
</script>

<style scoped lang="scss">
.tti-phonecc-label {
  height: 32px;
  margin-top: 0;
  margin-bottom: 8px;
  line-height: 32px;
  font-size: $FS01;
}
</style>
